// Duration picker component with single-column dropdown interface (no header)
// UPDATED: Removed header from dropdown to maximize list space
// FIXED: Click outside detection now properly includes dropdown portal ref
// Features: Smart vertical positioning, keyboard navigation, mobile-responsive, auto-scroll to selected value
// Displays durations from 15 min to 8h in 15-minute intervals
// Portal ensures dropdown is always visible even in modals with overflow constraints

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Timer, X } from 'lucide-react';
import { getPortalRoot } from '../../lib/portalUtils';
import { formatDuration } from '../../lib/dateUtils';

interface DurationPickerProps {
  value: number | null;
  onChange: (minutes: number) => void;
  placeholder?: string;
  minDuration?: number;
  maxDuration?: number;
  step?: number;
  disabled?: boolean;
  className?: string;
  label?: string;
  required?: boolean;
}

export function DurationPicker({
  value,
  onChange,
  placeholder = 'Sélectionnez une durée',
  minDuration = 15,
  maxDuration = 480,
  step = 15,
  disabled = false,
  className = '',
  label,
  required = false,
}: DurationPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number; direction: 'bottom' | 'top' }>({
    top: 0,
    left: 0,
    width: 0,
    direction: 'bottom'
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Generate all durations from minDuration to maxDuration by step
  const durations = Array.from(
    { length: Math.floor((maxDuration - minDuration) / step) + 1 },
    (_, i) => minDuration + i * step
  );

  // Calculate optimal dropdown position when opening
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = 320;

      // Check vertical space
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const direction = spaceBelow >= dropdownHeight || spaceBelow > spaceAbove ? 'bottom' : 'top';

      // Calculate position
      const top = direction === 'bottom'
        ? rect.bottom + 8 // 8px gap below button
        : rect.top - dropdownHeight - 8; // 8px gap above button

      setDropdownPosition({
        top,
        left: rect.left,
        width: rect.width,
        direction
      });

      // Auto-scroll to selected value
      setTimeout(() => {
        if (value !== null && value > 0 && listRef.current) {
          const selectedElement = listRef.current.querySelector(`[data-duration="${value}"]`);
          selectedElement?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      }, 50);
    }
  }, [isOpen, value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsideContainer = containerRef.current && !containerRef.current.contains(target);
      const isOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(target);

      if (isOutsideContainer && isOutsideDropdown) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen]);

  const handleDurationSelect = (duration: number) => {
    onChange(duration);
    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(0);
    setIsOpen(false);
  };

  const displayValue = value !== null && value > 0 ? formatDuration(value) : placeholder;
  const hasValue = value !== null && value > 0;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full flex items-center gap-3 px-4 py-2 border rounded-lg transition-all text-sm ${
          disabled
            ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
            : hasValue
            ? 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none'
            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none'
        }`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={label || 'Sélectionner une durée'}
      >
        <Timer size={18} className={hasValue ? 'text-gray-600' : 'text-gray-400'} />
        <span className="flex-1 text-left">{displayValue}</span>
        {hasValue && !disabled && (
          <X
            size={18}
            className="text-gray-400 hover:text-gray-600"
            onClick={handleClear}
          />
        )}
      </button>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="bg-white border border-gray-200 rounded-lg shadow-xl"
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            zIndex: 9999,
            maxHeight: '320px'
          }}
        >
          {/* Single Duration Column */}
          <div
            ref={listRef}
            className="overflow-y-auto"
            style={{ maxHeight: '320px' }}
            role="listbox"
            aria-label="Durées disponibles"
          >
            {durations.map((duration) => (
              <button
                key={duration}
                type="button"
                data-duration={duration}
                onClick={() => handleDurationSelect(duration)}
                className={`w-full px-4 py-2 text-sm transition-colors text-left ${
                  value === duration
                    ? 'bg-primary-500 text-white font-semibold'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                role="option"
                aria-selected={value === duration}
              >
                {formatDuration(duration)}
              </button>
            ))}
          </div>
        </div>,
        getPortalRoot()
      )}
    </div>
  );
}
